import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { fetchCollection, getLocalized } from '../lib/cms'
import ContactForm from '../components/ContactForm'
import ScrollReveal from '../components/ScrollReveal'
import RelatedServices from '../components/RelatedServices'
import FAQSection from '../components/FAQSection'
import { updateMeta } from '../lib/utils'
import { 
  Calculator, 
  Percent, 
  FileText, 
  FileCheck, 
  TrendingUp, 
  Users, 
  Briefcase, 
  ArrowLeft, 
  Check, 
  ChevronRight
} from 'lucide-react'

const iconMap = {
  Calculator,
  Percent,
  FileText,
  FileCheck,
  TrendingUp,
  Users,
  Briefcase
}

export default function ServiceDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [service, setService] = useState(null)
  const [allServices, setAllServices] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadServiceData() {
      setLoading(true)
      try {
        const servicesData = await fetchCollection('services')
        setAllServices(servicesData)
        
        // Omdiriger automatisk CMS-slugs til de rige SEO-hovedsider, hvis de matcher
        if (slug === 'bogfoering' || slug === 'hjaelp-til-bogfoering') {
          navigate('/bogfoering', { replace: true })
          return
        }
        if (slug === 'aarsregnskab-og-bogfoering' || slug === 'regnskabsservice') {
          navigate('/regnskabsservice', { replace: true })
          return
        }
        if (slug === 'aarsregnskab' || slug === 'aarsrapport' || slug === 'aarsafslutning') {
          navigate('/aarsrapport-og-aarsregnskab', { replace: true })
          return
        }

        // Find the service matching the slug
        const foundService = servicesData.find(
          (s) => s.slug?.da === slug || s.slug === slug
        )
        
        if (foundService) {
          setService(foundService)
        } else {
          setService(null)
        }
      } catch (error) {
        console.error('Error loading service detail:', error)
      } finally {
        setLoading(false)
      }
    }
    loadServiceData()
  }, [slug])

  useEffect(() => {
    if (service) {
      const title = getLocalized(service.title) || 'Ydelse'
      
      let metaTitle = `${title} | Thellefsen Regnskab`
      let metaDesc = ''

      if (slug === 'aarsregnskab') {
        metaTitle = "Årsregnskab | Thellefsen Regnskab"
        metaDesc = "Professionel opstilling og rettidig indberetning af årsregnskab til Erhvervsstyrelsen efter gældende regler til en fast årlig pris."
      } else if (slug === 'aarsregnskab-og-bogfoering') {
        metaTitle = "Regnskab & Bogføring | Fast pris"
        metaDesc = "Komplet regnskabspakke med løbende bogføring, moms og årsregnskab til fast pris. Få fuld ro i maven og en fast personlig sparringspartner."
      } else if (slug === 'personlig-selvangivelse') {
        metaTitle = "Selvangivelse & Skat | Fast pris"
        metaDesc = "Hjælp til den personlige selvangivelse, forskudsopgørelse, udlejningsejendom og værdipapirer. Få overblik og alle dine berettigede fradrag."
      } else {
        const rawDesc = (getLocalized(service.description) || '').replace(/<[^>]*>/g, '').trim()
        metaDesc = rawDesc || `Få professionel hjælp til ${title}. Vi sikrer korrekt indberetning, optimering af fradrag og personlig rådgivning til fast pris.`
      }
      
      updateMeta(metaTitle, metaDesc)
    } else {
      updateMeta(
        "Ydelser | Thellefsen Regnskab",
        "Læs mere om vores professionelle regnskabsydelser og faste priser til mindre selskaber og enkeltmandsvirksomheder."
      )
    }
  }, [service, slug])

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="h-10 w-32 bg-stone-200 animate-pulse mb-8 rounded-none" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-6">
              <div className="h-16 w-2/3 bg-stone-200 animate-pulse rounded-none" />
              <div className="h-6 w-1/2 bg-stone-200 animate-pulse rounded-none" />
              <div className="h-96 bg-stone-200 animate-pulse rounded-none" />
            </div>
            <div className="lg:col-span-4 h-96 bg-stone-200 animate-pulse rounded-none" />
          </div>
        </div>
      </div>
    )
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center px-4 py-20 text-center">
        <h1 className="text-4xl font-bold text-stone-900 mb-4">Ydelsen blev ikke fundet</h1>
        <p className="text-stone-600 mb-8 max-w-md">
          Vi kunne desværre ikke finde den specifikke ydelse, du leder efter. Se alle vores ydelser på oversigten.
        </p>
        <Link
          to="/ydelser"
          className="inline-flex items-center gap-2 rounded-none bg-orange-600 px-6 py-3 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
        >
          <ArrowLeft className="h-4 w-4" />
          Se alle ydelser
        </Link>
      </div>
    )
  }

  const IconComponent = iconMap[service.icon_name] || Calculator
  const otherServices = allServices.filter((s) => s.id !== service.id)

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      {/* Breadcrumb & Hero */}
      <section className="bg-white border-b border-stone-200 py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link
            to="/ydelser"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-500 hover:text-orange-600 transition-colors mb-8 group"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Tilbage til ydelser
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="space-y-4 max-w-3xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight">
                {getLocalized(service.title)}
              </h1>
              {service.sub_title && (
                <p className="text-lg sm:text-xl font-medium text-stone-900">
                  {getLocalized(service.sub_title)}
                </p>
              )}
              <p className="text-stone-600 leading-relaxed text-base sm:text-lg">
                {getLocalized(service.description)} Få personlig og professionel hjælp hos{' '}
                <Link to="/" className="text-stone-900 font-semibold hover:text-orange-600 transition-colors underline decoration-stone-300 underline-offset-2">
                  Thellefsen Regnskab
                </Link>.
              </p>
            </div>
            
            <div className="shrink-0">
              <a
                href="#kontakt-form-section"
                className="inline-flex items-center justify-center rounded-none bg-orange-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                Få et uforpligtende tilbud
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Rich Text Content */}
            <div className="lg:col-span-8 bg-white border border-stone-200 p-8 sm:p-12 shadow-sm">
              <ScrollReveal direction="up" duration={600}>
                <div 
                  className="space-y-6 
                    [&_h3]:text-2xl [&_h3]:font-bold [&_h3]:text-stone-900 [&_h3]:mt-8 [&_h3]:mb-4 [&_h3]:border-b [&_h3]:border-stone-100 [&_h3]:pb-2
                    [&_p]:text-stone-600 [&_p]:leading-relaxed [&_p]:mb-4
                    [&_ul]:list-none [&_ul]:pl-0 [&_ul]:space-y-3 [&_ul]:my-6
                    [&_li]:relative [&_li]:pl-7 [&_li]:text-stone-600 [&_li]:text-sm sm:[&_li]:text-base
                    [&_li_strong]:text-stone-900
                    [&_li]:before:content-['✓'] [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:text-orange-600 [&_li]:before:font-bold [&_li]:before:text-lg"
                  dangerouslySetInnerHTML={{ __html: getLocalized(service.content) }}
                />
              </ScrollReveal>
            </div>

            {/* Right Column: Sticky Sidebar */}
            <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-28">
              
              {/* Other Services Navigation */}
              <ScrollReveal direction="up" delay={200} duration={600}>
                <div className="bg-white border border-stone-200 p-8 shadow-sm">
                  <h3 className="text-lg font-bold text-stone-900 mb-4">Vores andre ydelser</h3>
                  <div className="divide-y divide-stone-100">
                    <Link
                      to="/regnskabsservice"
                      className="flex items-center justify-between py-3 text-sm text-stone-600 hover:text-orange-600 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <Briefcase className="h-4 w-4 text-orange-600" />
                        <span className="font-semibold text-stone-900 group-hover:text-orange-600">Regnskabsservice (Samlet)</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-stone-400 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                      to="/bogfoering"
                      className="flex items-center justify-between py-3 text-sm text-stone-600 hover:text-orange-600 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <FileCheck className="h-4 w-4 text-stone-400 group-hover:text-orange-600 transition-colors" />
                        <span className="font-medium">Bogføring & moms</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-stone-400 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                      to="/aarsrapport-og-aarsregnskab"
                      className="flex items-center justify-between py-3 text-sm text-stone-600 hover:text-orange-600 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-stone-400 group-hover:text-orange-600 transition-colors" />
                        <span className="font-medium">Årsrapport & årsregnskab</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-stone-400 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    {otherServices.map((s) => {
                      const OtherIcon = iconMap[s.icon_name] || Calculator
                      return (
                        <Link
                          key={s.id}
                          to={`/ydelser/${s.slug?.da || s.slug}`}
                          className="flex items-center justify-between py-3 text-sm text-stone-600 hover:text-orange-600 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            <OtherIcon className="h-4 w-4 text-stone-400 group-hover:text-orange-600 transition-colors" />
                            <span className="font-medium">{getLocalized(s.title)}</span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-stone-400 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </ScrollReveal>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title={`Ofte stillede spørgsmål om ${getLocalized(service.title).toLowerCase()}`}
        subtitle={`Få svar på de mest almindelige spørgsmål vedrørende ${getLocalized(service.title).toLowerCase()} og vores rådgivning.`}
        items={
          slug === 'aarsregnskab' ? [
            {
              question: "Hvem er forpligtet til at få udarbejdet et officielt årsregnskab?",
              answer: "Alle kapitalselskaber såsom ApS og A/S har pligt til at udarbejde og indsende en officiel årsrapport til Erhvervsstyrelsen. Enkeltmandsvirksomheder skal ikke indsende offentligt regnskab, men skal udarbejde et skattemæssigt årsregnskab til brug for oplysningsskemaet og beregning af virksomhedens overskud hos SKAT."
            },
            {
              question: "Hvad koster udarbejdelse og indberetning af et årsregnskab?",
              answer: "Hos Thellefsen Regnskab starter et årsregnskab for en mindre enkeltmandsvirksomhed fra 4.000 kr. ekskl. moms. For selskaber (ApS) afhænger prisen af bilagsvolumen og eventuelt behov for forudgående afstemning, men vi giver altid et fast skriftligt tilbud forud for opgavens påbegyndelse."
            },
            {
              question: "Hvordan sikrer I, at alle berettigede fradrag udnyttes optimalt?",
              answer: "Vi gennemgår alle årets posteringer, driftsomkostninger og investeringer i inventar eller udstyr for at sikre fuldt fradrag. Vi rådgiver desuden om skattemæssige afskrivninger, tabsfradrag og eventuelle fremførte underskud, så du betaler mindst muligt i skat helt inden for lovens rammer."
            },
            {
              question: "Hvad sker der, hvis årsregnskabet indsendes for sent til myndighederne?",
              answer: "Overskrides fristen for selskaber (6 måneder efter regnskabsårets afslutning), pålægger Erhvervsstyrelsen ledelsen personlige afgiftsbøder, der stiger måned for måned, og selskabet kan i værste fald sendes til tvangsopløsning. Vi sørger for at have dine tal klar i god tid, så du undgår enhver form for sanktioner."
            },
            {
              question: "Kræver mit årsregnskab en påtegning fra en statsautoriseret revisor?",
              answer: "Nej, langt de fleste mindre virksomheder og selskaber kan lovligt fravælge revisionspligten og derved spare store beløb på revisionshonoraret. Vi hjælper dig med at kontrollere, at dit selskab opfylder betingelserne for fravalg af revision, og udarbejder ledelsespåtegningen i årsrapporten korrekt."
            }
          ] : slug === 'aarsregnskab-og-bogfoering' ? [
            {
              question: "Hvorfor vælge en kombineret pakke med både bogføring og årsregnskab?",
              answer: "En samlet pakke sikrer fuld kontinuitet og ro i maven, fordi den samme person kender dine tal fra januar til december. Du slipper for at skulle overdrage uafstemte bilag til en fremmed revisor ved årets udgang, og processen omkring både moms og årsregnskab glider uden forsinkelser eller ekstraregninger."
            },
            {
              question: "Hvordan fungerer den løbende overdragelse af bilag og fakturaer?",
              answer: "Du overdrager dine bilag helt digitalt via din smartphone eller e-mail, hvorefter de automatisk lander i regnskabsprogrammet. Du tager blot et billede af kvitteringen på farten eller videresender PDF-fakturaer, hvorefter vi bogfører og afstemmer mod banken med jævne mellemrum."
            },
            {
              question: "Er momsindberetning og afstemning fuldt inkluderet i pakken?",
              answer: "Ja, vi afstemmer samtlige salgs- og købsmomsbeløb ved hver momsperiode og indberetter direkte til Skattestyrelsen inden for de gældende tidsfrister. Vi orienterer dig altid om det præcise momsbeløb i god tid, så du kan nå at reservere likviditeten på din virksomhedskonto."
            },
            {
              question: "Får jeg et gratis regnskabsprogram med i den samlede faste pris?",
              answer: "Ja, en standardlicens til et førende regnskabsprogram som Dinero eller Billy er inkluderet uden merpris, når du vælger vores faste pakkeløsning. Vi hjælper gerne med hele den tekniske opsætning, kontoplan og integration til din erhvervskonto, så systemet arbejder optimalt for dig."
            },
            {
              question: "Hvad sker der, hvis jeg får spørgsmål til min økonomi i løbet af året?",
              answer: "Løbende telefonisk sparring og hurtige svar på e-mail er en naturlig del af vores service. Du ringer eller skriver blot direkte til Peter, hvis du er i tvivl om et købsfradrag, kørselspenge eller fakturering, og får et hurtigt svar uden at skulle frygte en tikkende timepris."
            }
          ] : slug === 'personlig-selvangivelse' ? [
            {
              question: "Hvem har særligt gavn af professionel hjælp til den personlige selvangivelse?",
              answer: "Hjælp er særligt værdifuld for selvstændige erhvervsdrivende, personer med udlejningsejendom eller forældrekøb, udenlandsk indkomst eller større investeringer i aktier og værdipapirer. Her overser mange skatteyderne ofte store fradragsmuligheder eller risikerer restskat pga. forkerte feltindberetninger hos SKAT."
            },
            {
              question: "Hvordan hjælper I med beskatning af udlejningsejendom og forældrekøb?",
              answer: "Vi opstiller et korrekt ejendomsregnskab over lejeindtægter, vedligeholdelsesudgifter og renteudgifter. Vi rådgiver om den mest fordelagtige beskatningsform – f.eks. virksomhedsordningen (VSO), kapitalafkastordningen eller almindelig personbeskatning – så du opnår den lavest mulige beskatning."
            },
            {
              question: "Kan I hjælpe med skatteforhold ved arbejde i udlandet (§ 33A)?",
              answer: "Ja, arbejder du helt eller delvist i udlandet, sikrer vi, at reglerne i ligningslovens § 33A eller gældende dobbeltbeskatningsoverenskomster anvendes korrekt. Vi beregner dine skattelempelser og dokumenterer dine rejsedage og arbejdsforhold grundigt over for Skattestyrelsen."
            },
            {
              question: "Hvordan håndterer I beskatning af aktier, obligationer og værdipapirer?",
              answer: "Vi gennemgår dine gevinster, tab og udbytter på både danske og udenlandske depoter samt medarbejderaktier og warrants. Vi sikrer, at tab modregnes korrekt i gevinster, og at beskatningen sker efter de rigtige principper (realisation eller lagerbeskatning), så du undgår skattefejl."
            },
            {
              question: "Hjælper I også med at tilpasse og rette min løbende forskudsopgørelse?",
              answer: "Ja, hvis din indkomst, renteudgifter eller virksomhedsresultat ændrer sig i løbet af året, justerer vi din forskudsopgørelse. På den måde betaler du den korrekte B-skat løbende igennem året og undgår kedelige overraskelser med renter og restskat, når årsopgørelsen dannes i foråret."
            }
          ] : [
            {
              question: `Hvordan kommer jeg i gang med ${getLocalized(service.title).toLowerCase()}?`,
              answer: "Du kontakter os blot via telefon eller vores kontaktformular, hvorefter vi tager en kort, uforpligtende snak om dine konkrete behov. Vi giver dig en fast pris og en klar tidsplan, så du ved præcis, hvad der skal ske, og hvornår opgaven er løst."
            },
            {
              question: "Er prisen fast, eller risikerer jeg ekstraregninger?",
              answer: "Hos Thellefsen Regnskab arbejder vi med faste, aftalte priser forud for opgavens start. Du modtager altid et skriftligt tilbud, og hvis der uventet opstår ekstraarbejde, drøfter og aftaler vi det altid med dig på forhånd, så der aldrig opstår ubehagelige overraskelser."
            },
            {
              question: "Hvem udfører arbejdet og står for rådgivningen?",
              answer: "Det er altid Peter Thellefsen personligt, der varetager dine opgaver og rådgiver dig. Du har ét fast kontaktpunkt, som kender din forretning i detaljer, og du slipper for at skulle forklare din situation forfra til skiftende medarbejdere."
            },
            {
              question: "Hvordan sikrer I overholdelse af gældende lovgivning?",
              answer: "Vi holder os løbende opdateret på de nyeste regler inden for skat, moms og regnskabslovgivning. Vi sikrer, at alle indberetninger opfylder myndighedernes officielle krav, så du trygt kan lægge ansvaret fra dig og koncentrere dig om din egen virksomhed."
            },
            {
              question: "Hvor hurtigt kan I levere det færdige resultat?",
              answer: "Vi arbejder hurtigt og effektivt. Så snart vi har modtaget det nødvendige materiale og oplysninger fra dig, påbegynder vi arbejdet og overholder altid de aftalte tidsfrister, så du slipper for unødig ventetid og stress."
            }
          ]
        }
      />

      {/* Related Services */}
      <RelatedServices currentPath={`/ydelser/${slug}`} title="Se også vores andre ydelser" />

      {/* Contact Section */}
      <section id="kontakt-form-section" className="py-16 lg:py-24 bg-white border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
                Uforpligtende tilbud
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                Klar til at få hjælp til {getLocalized(service.title).toLowerCase()}?
              </h2>
              <p className="text-stone-600 leading-relaxed text-base sm:text-lg">
                Udfyld formularen, så vender vi tilbage inden for 24 timer med et uforpligtende tilbud eller svar på dine spørgsmål.
              </p>
            </div>
            <div className="lg:col-span-7 bg-stone-50 border border-stone-200 p-8 sm:p-10 shadow-sm">
              <ContactForm defaultService={slug} />
            </div>
          </div>
        </div>
      </section>

    </div>
  )
}
